/**
 * COMPONENT: c-timeline (Tier 3 — Full Web Component)
 * Auto-advancing timeline with positioned points that fade in/out.
 *
 * Above 768px: auto-advances through points on a timer.
 * Below 768px: animation destroyed, all points visible, no JS behavior.
 * Rebuilds/destroys on resize across the breakpoint.
 *
 * Click behavior (when active):
 *   - Stops auto-advance permanently
 *   - Clicked point toggles visibility
 *   - Clicking another point hides current, shows clicked
 *
 * Attributes:
 *   speed      — hold time per point in ms (default: 3000)
 *   transition — fade time between points in ms (default: 500)
 *
 * State attributes (set by JS, consumed by CSS):
 *   [paused]   — freezes on current point
 *   [stopped]  — permanently stopped (click)
 *   [last]     — active point is the last in the sequence
 *   [destroyed] — below breakpoint, all JS behavior disabled
 */

class Timeline extends HTMLElement {

  constructor() {
    super();
    this._points = [];
    this._activeIndex = -1;
    this._timer = null;
    this._phase = 'idle';
    this._paused = false;
    this._stopped = false;
    this._pauseAfterTransition = false;
    this._speedStartTime = 0;
    this._speedRemaining = 0;
    this._initialized = false;
    this._mq = window.matchMedia('(min-width: 769px)');
    this._boundHandleBreakpoint = this._handleBreakpoint.bind(this);
  }

  connectedCallback() {
    this._points = [...this.querySelectorAll('._point')];
    if (!this._points.length) return;

    // Bind click/keyboard handlers once (they check _initialized internally)
    this._points.forEach((point, i) => {
      point.setAttribute('tabindex', '0');
      point.setAttribute('role', 'button');
      point.addEventListener('click', () => this._handleClick(i));
      point.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._handleClick(i);
        }
      });
    });

    // Listen for breakpoint changes
    this._mq.addEventListener('change', this._boundHandleBreakpoint);

    // Initialize based on current viewport
    this._handleBreakpoint(this._mq);
  }

  disconnectedCallback() {
    this._destroy();
    this._mq.removeEventListener('change', this._boundHandleBreakpoint);
  }

  get speed() {
    return parseInt(this.getAttribute('speed')) || 3000;
  }

  get transition() {
    return parseInt(this.getAttribute('transition')) || 500;
  }

  // -----------------------------------------------------------
  // RESPONSIVE INIT / DESTROY
  // -----------------------------------------------------------

  _handleBreakpoint(e) {
    if (e.matches) {
      this._init();
    } else {
      this._destroy();
    }
  }

  _init() {
    if (this._initialized) return;
    this._initialized = true;

    this.removeAttribute('destroyed');

    const speed = parseInt(this.getAttribute('speed')) || 3000;
    const transition = parseInt(this.getAttribute('transition')) || 500;

    this.style.setProperty('--tl-speed', speed + 'ms');
    this.style.setProperty('--tl-transition', transition + 'ms');

    // Reset state
    this._stopped = false;
    this._paused = false;
    this._pauseAfterTransition = false;
    this._activeIndex = -1;
    this.removeAttribute('stopped');
    this.removeAttribute('paused');

    // Bind hover/focus per point
    this._points.forEach(point => {
      point.addEventListener('mouseenter', this._handlePause);
      point.addEventListener('mouseleave', this._handleResume);
      point.addEventListener('focusin', this._handlePause);
      point.addEventListener('focusout', this._handleResume);
    });

    this._activate(0);
    this._startSpeedPhase();

    // Respect reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._clearTimer();
      this._stopped = true;
      this.setAttribute('stopped', '');
    }
  }

  _destroy() {
    if (!this._initialized) return;
    this._initialized = false;

    this._clearTimer();
    this.setAttribute('destroyed', '');
    this.removeAttribute('stopped');
    this.removeAttribute('paused');
    this.removeAttribute('last');

    // Clear all point states
    this._points.forEach(point => {
      point.classList.remove('-active', '-exiting');
      point.removeAttribute('aria-current');
      point.removeEventListener('mouseenter', this._handlePause);
      point.removeEventListener('mouseleave', this._handleResume);
      point.removeEventListener('focusin', this._handlePause);
      point.removeEventListener('focusout', this._handleResume);
      const content = point.querySelector('div');
      if (content) content.removeAttribute('aria-hidden');
    });

    this._stopped = false;
    this._paused = false;
    this._pauseAfterTransition = false;
    this._activeIndex = -1;
    this._phase = 'idle';
  }

  // -----------------------------------------------------------
  // ACTIVATION
  // -----------------------------------------------------------

  _activate(index) {
    this._points.forEach(point => {
      point.classList.remove('-active', '-exiting');
      point.removeAttribute('aria-current');
      const content = point.querySelector('div');
      if (content) content.setAttribute('aria-hidden', 'true');
    });

    const target = this._points[index];
    target.classList.add('-active');
    target.setAttribute('aria-current', 'step');
    const content = target.querySelector('div');
    if (content) content.removeAttribute('aria-hidden');

    this._activeIndex = index;
    this.toggleAttribute('last', index === this._points.length - 1);
  }

  _deactivateAll() {
    this._points.forEach(point => {
      point.classList.remove('-active', '-exiting');
      point.removeAttribute('aria-current');
      const content = point.querySelector('div');
      if (content) content.setAttribute('aria-hidden', 'true');
    });
    this._activeIndex = -1;
  }

  _nextIndex() {
    return (this._activeIndex + 1) % this._points.length;
  }

  // -----------------------------------------------------------
  // PHASE MANAGEMENT
  // -----------------------------------------------------------

  _startSpeedPhase() {
    this._phase = 'speed';
    this._speedStartTime = performance.now();
    this._speedRemaining = this.speed;

    this.removeAttribute('paused');

    this._timer = setTimeout(() => {
      this._startTransitionPhase();
    }, this._speedRemaining);
  }

  _startTransitionPhase() {
    this._phase = 'transition';

    this._points[this._activeIndex].classList.add('-exiting');

    this._timer = setTimeout(() => {
      this._activate(this._nextIndex());

      if (this._pauseAfterTransition) {
        this._pauseAfterTransition = false;
        this._phase = 'speed';
        this._speedRemaining = this.speed;
        this.setAttribute('paused', '');
        return;
      }

      this._startSpeedPhase();
    }, this.transition);
  }

  // -----------------------------------------------------------
  // CLICK — TOGGLE & STOP
  // -----------------------------------------------------------

  _handleClick(clickedIndex) {
    if (!this._initialized) return;

    // First click: stop auto-advance
    if (!this._stopped) {
      this._clearTimer();
      this._stopped = true;
      this._paused = false;
      this._pauseAfterTransition = false;
      this.setAttribute('stopped', '');
      this.removeAttribute('paused');
    }

    // Toggle: clicking the active point hides it
    if (clickedIndex === this._activeIndex) {
      this._deactivateAll();
      return;
    }

    // Show clicked point (hides any currently active)
    this._activate(clickedIndex);
  }

  // -----------------------------------------------------------
  // PAUSE / RESUME
  // -----------------------------------------------------------

  _handlePause = () => {
    if (this._paused || this._stopped || !this._initialized) return;
    this._paused = true;

    if (this._phase === 'speed') {
      this._clearTimer();
      const elapsed = performance.now() - this._speedStartTime;
      this._speedRemaining = Math.max(0, this._speedRemaining - elapsed);
      this.setAttribute('paused', '');

    } else if (this._phase === 'transition') {
      this._pauseAfterTransition = true;
    }
  };

  _handleResume = () => {
    if (!this._paused || this._stopped || !this._initialized) return;

    const stillEngaged = this._points.some(
      p => p.matches(':hover') || p.contains(document.activeElement)
    );
    if (stillEngaged) return;

    this._paused = false;
    this._pauseAfterTransition = false;

    if (this._phase === 'speed') {
      this._speedStartTime = performance.now();
      this.removeAttribute('paused');

      this._timer = setTimeout(() => {
        this._startTransitionPhase();
      }, this._speedRemaining);
    }
  };

  // -----------------------------------------------------------
  // CLEANUP
  // -----------------------------------------------------------

  _clearTimer() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }
}

window.customElements.define('c-timeline', Timeline);